import SEO from './components/SEO';

export default function CookiePolicy() {
  return (
    <>
      <SEO 
        title="Cookie Policy"
        description="Learn about how Capstone Pakistan uses cookies and similar technologies to enhance your browsing experience and improve our services."
        keywords="cookie policy, cookies, tracking technologies, website analytics, Capstone Pakistan, privacy"
      />
      
      <div className="bg-gradient-to-br from-blue-50 to-indigo-100 min-h-screen py-16">
        <div className="max-w-4xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Cookie Policy</h1>
            <p className="text-lg text-gray-600">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>

          {/* Content */}
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 space-y-8">
            
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Introduction</h2>
              <p className="text-gray-700 leading-relaxed">
                This Cookie Policy explains how Capstone Pakistan uses cookies and similar technologies when you visit our website. By using our website, you consent to the use of cookies in accordance with this policy.
              </p>
            </section>

            {/* What Are Cookies */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">What Are Cookies?</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Cookies are small text files that are stored on your device (computer, tablet, or mobile phone) when you visit a website. They help websites remember information about your visit, such as your preferred language and other settings, which can make your next visit easier and more useful.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Cookies can be set by the website you are visiting (first-party cookies) or by third-party services that the website uses (third-party cookies).
              </p>
            </section>

            {/* Types of Cookies We Use */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Types of Cookies We Use</h2>
              
              <h3 className="text-xl font-medium text-gray-800 mb-3">Essential Cookies</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                These cookies are necessary for the website to function properly. They enable basic functions like page navigation, access to secure areas, and form submissions. The website cannot function properly without these cookies.
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
                <li>Session management and security</li>
                <li>User authentication and login</li>
                <li>Form submission and validation</li>
                <li>Language and region preferences</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">Analytics Cookies</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
                <li>Page views and time spent on pages</li>
                <li>Traffic sources and user behavior</li>
                <li>Website performance and error tracking</li>
                <li>Popular content and features</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">Functional Cookies</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                These cookies enable enhanced functionality and personalization, such as remembering your preferences and settings.
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
                <li>User preferences and settings</li>
                <li>Competition registration information</li>
                <li>Newsletter subscription status</li>
                <li>Social media integration</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">Marketing Cookies</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                These cookies are used to track visitors across websites to display relevant and engaging advertisements.
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Social media advertising</li>
                <li>Email marketing campaigns</li>
                <li>Competition promotion tracking</li>
                <li>User engagement analytics</li>
              </ul>
            </section>

            {/* Specific Cookies We Use */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Specific Cookies We Use</h2>
              
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Cookie Name</th>
                      <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Purpose</th>
                      <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">session_id</td>
                      <td className="border border-gray-300 px-4 py-2">Maintains user session and authentication</td>
                      <td className="border border-gray-300 px-4 py-2">Session</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-2">language_pref</td>
                      <td className="border border-gray-300 px-4 py-2">Stores user's language preference</td>
                      <td className="border border-gray-300 px-4 py-2">1 year</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">_ga</td>
                      <td className="border border-gray-300 px-4 py-2">Google Analytics - tracks website usage</td>
                      <td className="border border-gray-300 px-4 py-2">2 years</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-2">_gid</td>
                      <td className="border border-gray-300 px-4 py-2">Google Analytics - session tracking</td>
                      <td className="border border-gray-300 px-4 py-2">24 hours</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">competition_reg</td>
                      <td className="border border-gray-300 px-4 py-2">Stores competition registration progress</td>
                      <td className="border border-gray-300 px-4 py-2">30 days</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-2">newsletter_opt</td>
                      <td className="border border-gray-300 px-4 py-2">Newsletter subscription preference</td>
                      <td className="border border-gray-300 px-4 py-2">1 year</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Third-Party Cookies */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Third-Party Cookies</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Our website may use third-party services that set their own cookies. These services include:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li><strong>Google Analytics:</strong> For website analytics and performance tracking</li>
                <li><strong>Social Media Platforms:</strong> For social sharing and integration features</li>
                <li><strong>Email Marketing Services:</strong> For newsletter and communication management</li>
                <li><strong>Payment Processors:</strong> For competition registration payments (if applicable)</li>
                <li><strong>Content Delivery Networks:</strong> For faster website loading and performance</li>
              </ul>
            </section>

            {/* Cookie Management */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Managing Your Cookie Preferences</h2>
              
              <h3 className="text-xl font-medium text-gray-800 mb-3">Browser Settings</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                You can control and manage cookies through your browser settings. Most browsers allow you to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
                <li>View and delete existing cookies</li>
                <li>Block cookies from specific websites</li>
                <li>Block all cookies</li>
                <li>Set preferences for different types of cookies</li>
                <li>Receive notifications when cookies are set</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">Browser-Specific Instructions</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
                <li><strong>Chrome:</strong> Settings → Privacy and security → Cookies and other site data</li>
                <li><strong>Firefox:</strong> Options → Privacy & Security → Cookies and Site Data</li>
                <li><strong>Safari:</strong> Preferences → Privacy → Manage Website Data</li>
                <li><strong>Edge:</strong> Settings → Cookies and site permissions → Cookies and site data</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">Cookie Consent</h3>
              <p className="text-gray-700 leading-relaxed">
                When you first visit our website, you will see a cookie consent banner. You can choose which types of cookies to accept or reject. You can change your preferences at any time by clicking the "Cookie Settings" link in our footer.
              </p>
            </section>

            {/* Impact of Disabling Cookies */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Impact of Disabling Cookies</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you choose to disable cookies, some features of our website may not function properly:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>You may need to log in repeatedly</li>
                <li>Your preferences and settings may not be saved</li>
                <li>Some website features may not work as expected</li>
                <li>We may not be able to provide personalized content</li>
                <li>Website performance may be affected</li>
              </ul>
            </section>

            {/* Updates to Cookie Policy */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Updates to This Cookie Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Cookie Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by posting the updated policy on our website and updating the "Last updated" date.
              </p>
            </section>

            {/* Contact Information */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Us</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have any questions about our use of cookies or this Cookie Policy, please contact us:
              </p>
              <div className="bg-gray-50 rounded-lg p-6">
                <p className="text-gray-700 mb-2">
                  <strong>Email:</strong> <a href="mailto:admin@capstonepakistan.org" className="text-blue-600 hover:text-blue-800">admin@capstonepakistan.org</a>
                </p>
                <p className="text-gray-700 mb-2">
                  <strong>Organization:</strong> Capstone Pakistan
                </p>
                <p className="text-gray-700">
                  <strong>Address:</strong> Pakistan
                </p>
              </div>
            </section>

            {/* Related Policies */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Related Policies</h2>
              <p className="text-gray-700 leading-relaxed">
                For more information about how we handle your personal information, please review our <a href="/privacy-policy" className="text-blue-600 hover:text-blue-800 underline">Privacy Policy</a> and <a href="/terms-of-service" className="text-blue-600 hover:text-blue-800 underline">Terms of Service</a>.
              </p>
            </section>
          </div>
        </div>
      </div>
    </>
  );
} 